import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import {
  graphql, useRefetchableFragment,
} from 'react-relay';

import DashboardControl from 'src/components/Dashboard/DashboardControl';
import { WARNING_TIMEOUT, showTradeRuleMsg } from 'src/components/Dashboard/DashboardControl/helpers/common';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import Placeholder from 'src/components/Dashboard/Placeholder';
import { buildChartViewKey, buildStateURLParams, updateEntityShowState } from 'src/components/Dashboard/helpers/common';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import subHeading from 'src/components/Portfolio/helpers/common';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import FlashesStore from 'src/stores/FlashesStore';
import {
  MODAL_CLOSE_DELAY,
  P1D,
  PT30M, SOURCE, SOURCE_TRADES,
  TIME_ZONE_SYSTEM, UNIT, UNIT_CURRENCY, UNIT_ENERGY,
  VIEW_SELECTOR,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import formatAsyncErrorBodyText from 'src/util/i18n/formatAsyncErrorBodyText';
import { getDefaultLocale } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import { dateRangeToTimeRange, dateToTimeInTimezone } from 'src/util/time';

import PortfolioShowTradeEnergy from './PortfolioShowTradeEnergy';
import { buildMainData } from './data/mainDataBuilder';
import { buildLoadingOverlayState } from './helpers/buildLoadingOverlayState';
import { messages } from './helpers/constants';
import chartViewSelector from './helpers/portfolioShow';
import usePortfolioData from './hooks/usePortfolioData';
import {
  PortfolioShowLoadingActions,
  PortfolioShowLoadingStates,
  defaultInitialState,
} from './reducer/constants';
import loadingStateReducer from './reducer/portfolioShowLoadingReducer';
// Portfolio fragment for static data, not varying over time
const PortfolioShowFragment = graphql`
  fragment PortfolioShow on Query
  @throwOnFieldError
  @refetchable(queryName: "PortfolioShowRefetchQuery")
  @argumentDefinitions(
    uuid: { type: "ID!" }
  ) {
    portfolio(uuid: $uuid) {
      uuid
      externalIdentifier
      active { start finish }
      title
      propertyMembers(first: 500) {
        totalCount
        edges {
          node {
            uuid
            externalIdentifier
            active { start finish }
            property {
              uuid
              externalIdentifier
              uuid
              title
              publicHolidayRegion
            }
          }
        }
      }
      userMembers(first: 500) {
        totalCount
        edges {
          node {
            uuid
            externalIdentifier
            active { start finish }
            role
            user {
              uuid
              externalIdentifier
              email
              givenName
              familyName
              active { start finish }
            }
          }
        }
      }
    }
  }
`;

/**
 * Description
 * @param {object} props
 * @param {object} props.queryParams
 * @returns {React.ReactComponentElement} - PortfolioShow component
 */
function PortfolioShow({ queryParams = {}, ...props }) {
  // Environment is managed by usePortfolioData hook
  const {
    timezone: queryTimezone,
    aggregateBy: queryAggregateBy,
    groupBy: queryGroupBy,
    xAxisDomain: queryXAxisDomain,
    source: querySource,
    unit: queryUnit,
  } = queryParams;
  const [initialData] = useRefetchableFragment(PortfolioShowFragment, props);
  const initialView = {
    aggregateBy: queryAggregateBy,
    groupBy: queryGroupBy,
    xAxisDomain: queryXAxisDomain,
  };

  // Get portfolio ID from all possible URL sources
  const portfolioId = props?.match?.params?.uuid;
  const searchStr = props?.match?.location?.search || window.location.search;
  const urlParamsUuid = new URLSearchParams(searchStr).get('uuid');
  const actualPortfolioId = portfolioId || urlParamsUuid;
  const matchLocation = props?.match?.location || {};
  const searchParams = new URLSearchParams(matchLocation.search || window.location.search);

  // Get timezone information
  const portfolioTimezone = APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const timezone = queryTimezone || portfolioTimezone;

  // Setup datespan from URL or use defaults
  const startParam = searchParams.get('start');
  const finishParam = searchParams.get('finish');
  const defaultStart = DateTime.now().minus({ days: 6 }).setZone(timezone).startOf('day');
  // Date-range form: finish is the last day's midnight (start of day). The
  // exclusive next-day-midnight boundary used for fetching is derived via
  // dateRangeToTimeRange (finalDatespan), matching PropertyShow.
  const defaultFinish = DateTime.now().setZone(timezone).startOf('day');

  const intl = useIntl();

  // State to manage loading
  const [loadingState, dispatch] = useReducer(loadingStateReducer, defaultInitialState());

  // Whether to show the grouping message in the loading overlay.
  // The message varies based on rules count: trade-type grouping when count > 16,
  // counterparty grouping otherwise, or hidden when rules count is 0.
  const showGroupingMessageRef = useRef(false);

  // Parse selected keys from URL parameters (comma-separated format)
  // Only stores selectedKeys for the current view
  const cardSelectedKeysParam = searchParams.get('cardSelectedKeys');
  const initialSelectedKeys = useMemo(() => {
    if (!cardSelectedKeysParam) {
      return {};
    }

    // Build the chart view key for the current view.
    // Use the same source fallback as the `source` state (querySource || SOURCE_TRADES) so
    // that cardSelectedKeys is correctly restored from the URL even when the source param
    // is absent (the component defaults to SOURCE_TRADES in that case).
    const chartViewKey = buildChartViewKey(initialView, querySource || SOURCE_TRADES);

    if (!chartViewKey) {
      return {};
    }

    // Parse comma-separated keys
    const keysArray = cardSelectedKeysParam.split(',').filter((key) => key.trim() !== '');

    // Return selectedKeys object with current view's keys
    return keysArray.length > 0 ? { [chartViewKey]: keysArray } : {};
  }, [cardSelectedKeysParam, initialView, querySource]);

  const [selectedKeys, setSelectedKeys] = useState(initialSelectedKeys);

  // States for the component
  const [unit, setUnit] = useState(queryUnit || UNIT_ENERGY);
  const [source, setSource] = useState(querySource || SOURCE_TRADES);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(initialView);

  const firstTimeRender = useRef(true);

  // Process initial dates from URL
  const getInitialDatespan = () => {
    let start = defaultStart;
    let finish = defaultFinish;

    // Parse start date from URL if present
    if (startParam) {
      const parsedStart = DateTime.fromISO(startParam, { zone: timezone });
      if (parsedStart.isValid) {
        start = parsedStart.startOf('day');
      } else {
        logger.warn('Invalid start date parameter:', startParam);
      }
    }

    // Parse finish date from URL if present
    if (finishParam) {
      const parsedFinish = DateTime.fromISO(finishParam, { zone: timezone });
      if (parsedFinish.isValid) {
        finish = parsedFinish.startOf('day');
      } else {
        logger.warn('Invalid finish date parameter:', finishParam);
      }
    }

    logger.log('Setting initial datespan:', start.toISO(), 'to', finish.toISO());
    return { start, finish };
  };

  // Initial datespan state - ensure correct parsing of date parameters
  const [datespan, setDatespan] = useState(getInitialDatespan);

  // Convert the date-range datespan into a time range (finish at next-day
  // midnight, exclusive) for fetching, caching, and aggregation — matching
  // PropertyShow's finalDatespan. dateToTimeInTimezone normalises the time of
  // day, so the date-range finish can be any time on the last day.
  const finalDatespan = useMemo(
    () => dateRangeToTimeRange(datespan, timezone),
    [datespan, timezone],
  );

  // Compute aggregation from the time range so the threshold reflects whole days.
  const aggregation = useMemo(() => {
    const timespanDuration = finalDatespan.finish.diff(finalDatespan.start, 'days').days;
    return timespanDuration > 2 ? P1D : PT30M;
  }, [finalDatespan]);

  // This is a flag to prevent recursive calls from URL updates
  const lastUpdatedURL = useRef(null);

  // Snapshot of the last successfully loaded { start, finish } in date-range form.
  // Written by the snapshot effect below when the pipeline reaches COMPLETE with
  // valid data — mirroring PropertyShow's previousDatespanRef. Used by
  // restoreLastSuccessfulLoad to revert datespan without any inverse conversion.
  const previousDatespanRef = useRef(null);

  // Data fetching via custom hook
  const {
    combinedData, cancelFunc, clearError, error, handleRetry,
    isRetrying, queryType, getRecoveredSpanFromCache, applyRecoveredCacheData,
  } = usePortfolioData({
    portfolioId: actualPortfolioId,
    dateSpan: finalDatespan,
    timezone,
    initialData,
    historianAggregation: aggregation,
    tradeAggregation: aggregation,
    stateDispatchFunc: dispatch,
  });

  const { match } = props || {};

  // Update the URL with the new time range
  const updateURLWithTimeRange = useCallback((startTime, finishTime) => {
    try {
      // Check if we have props and match
      if (!match || !match.location) {
        logger.warn('Missing match or location for URL update, skipping');
        return;
      }

      // Using already destructured props
      const { location } = match;
      const { pathname } = location || { pathname: window.location.pathname };

      // Make sure dates are in the correct timezone for URL display
      const startInTimezone = startTime.setZone(timezone);
      const finishInTimezone = finishTime.setZone(timezone);

      // Generate URL signature to check if it changed
      const urlSignature = `${startInTimezone.toISODate()}-${finishInTimezone.toISODate()}-${timezone}`;

      // Skip URL update if it's identical to the last one
      if (lastUpdatedURL.current === urlSignature) {
        logger.log('Skipping duplicate URL update:', urlSignature);
        return;
      }

      // Store the current URL signature
      lastUpdatedURL.current = urlSignature;

      logger.log(
        'Updating URL with time range:',
        startInTimezone.toISODate(),
        'to',
        finishInTimezone.toISODate(),
        '(timezone:',
        timezone,
        ')',
      );

      const state = buildStateURLParams(
        chartView,
        startTime,
        finishTime,
        timezone,
        source,
        unit,
        selectedKeys,
      );

      if (!state) {
        logger.warn('Failed to generate URL state, skipping update');
        return;
      }

      // Create the search parameter string
      const searchString = `?${Object.keys(state)
        .map((k) => `${k}=${encodeURIComponent(state[k])}`)
        .join('&')}`;

      // Compare with current URL search to avoid unnecessary history updates
      const currentSearch = window.location.search;
      if (currentSearch === searchString) {
        logger.log('URL search params already match desired state, skipping update');
        return;
      }

      // Only update if BrowserProtocol is available
      if (BrowserProtocol && typeof BrowserProtocol.navigate === 'function') {
        // Make sure to include the action parameter
        BrowserProtocol.navigate({
          action: 'REPLACE', // Explicitly set the action parameter
          pathname,
          search: searchString,
          hash: '',
          state,
        });
      } else {
        logger.warn('BrowserProtocol not available, using history.replaceState');
        // Fallback to using history API directly
        window.history.replaceState(
          state,
          '',
          `${pathname}${searchString}`,
        );
      }
    } catch (urlError) {
      logger.error('Error updating URL:', urlError);
    }
  }, [match, timezone, chartView, source, unit, selectedKeys]);

  // Update URL when data arrives. DATA_PREPARED is dispatched by the overlay
  // lifecycle effect when state reaches PREPARING_DATA.
  useEffect(() => {
    if (combinedData) {
      updateURLWithTimeRange(datespan.start, datespan.finish);
    }
  }, [combinedData, datespan, updateURLWithTimeRange]);

  const updateURLState = useCallback(() => {
    updateEntityShowState(datespan, match, chartView, timezone, source, unit, selectedKeys);
  }, [datespan, match, chartView, timezone, source, unit, selectedKeys]);

  useEffect(() => {
    if (firstTimeRender.current) {
      firstTimeRender.current = false;
      return;
    }
    updateURLState();
  }, [updateURLState]);

  // Extract static portfolio fields from the initial fragment data (always available).
  // combinedData only has fetched data after the batched load completes.
  const initialPortfolio = initialData?.portfolio || {};
  const { active, title, uuid } = initialPortfolio;

  // Calculate counts straight from the error-checked fragment (available on mount).
  const propertyCount = initialPortfolio.propertyMembers?.totalCount ?? 0;
  const userCount = initialPortfolio.userMembers?.totalCount ?? 0;

  const toggle = (dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen(!viewSelectorOpen);
    } else {
      setDownloadSelectorOpen(!downloadSelectorOpen);
    }
  };

  const controlSetStateFunc = (opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;

    FlashesStore.reset();
    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        setTimeout(() => {
          setTradeBtnClicks(0);
        }, WARNING_TIMEOUT);
        btnClicks += 1;
      } else {
        showTradeRuleMsg(intl);
      }
    }
    if (group === UNIT) {
      chartUnit = value;
    }
    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }
    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  };

  const handleTimespanUpdate = (inputStart, inputFinish) => {
    // Validate inputs
    if (!inputStart || !inputFinish || !inputStart.isValid || !inputFinish.isValid) {
      logger.error('Invalid date inputs to handleTimespanUpdate:', { inputStart, inputFinish });
      return;
    }

    try {
      // Anchor both bounds to midnight on the selected calendar day in the
      // property timezone.
      //
      // Changed from `inputStart.setZone(timezone).startOf('day')` to
      // `dateToTimeInTimezone(inputStart, timezone)`. Why: setZone() preserves
      // the absolute instant before truncating, so a date carrying a different
      // zone (e.g. from the URL or another component) could round into an
      // adjacent calendar day. dateToTimeInTimezone reads only {year, month,
      // day} and rebuilds midnight in the property timezone, so the result is
      // always the exact day the user picked, independent of the input's zone.
      //
      // The finish stays in date-range form (midnight); the exclusive next-day
      // boundary is derived later via dateRangeToTimeRange.
      const updatedStart = dateToTimeInTimezone(inputStart, timezone);
      const updatedFinish = dateToTimeInTimezone(inputFinish, timezone);

      // Check if this is the same as the current datespan to avoid unnecessary updates
      const isSameStart = updatedStart.toMillis() === datespan.start.toMillis();
      const isSameFinish = updatedFinish.toMillis() === datespan.finish.toMillis();

      if (isSameStart && isSameFinish) {
        logger.log('Timespan update skipped - dates are identical');
        return;
      }

      // Check if it's a reasonable date range (not too large). updatedFinish is
      // date-range form (midnight), so add a day for the exclusive boundary to
      // count whole inclusive days, matching the aggregation calc.
      const spanDays = updatedFinish.plus({ days: 1 }).diff(updatedStart, 'days').days;
      if (spanDays > 60) {
        // diff() can yield fractional days (DST transitions / floating-point),
        // so round for display to avoid messages like "60.00000001 days".
        const displayDays = Math.round(spanDays);
        logger.warn(`Date range is very large (${displayDays} days). This might cause performance issues.`);
        FlashesStore.flash(
          FlashesStore.WARNING,
          `Selected date range is large (${displayDays} days). Consider selecting a smaller range for better performance.`,
        );
      }

      logger.log(
        'Timespan update - from user interaction:',
        updatedStart.toISO(),
        'to',
        updatedFinish.toISO(),
      );

      // Selecting a new date range is intentional, so any prior fetch error is
      // no longer relevant to the incoming request. Clearing it unmounts
      // PlaceholderErrorModal (which only closes itself locally and otherwise
      // leaves the parent error state intact) so the loading overlay is shown
      // and the fresh data renders once the fetch completes. START_REQUEST moves
      // the reducer out of the ERROR terminal state immediately, avoiding a flash
      // of empty chart content before the date-range effect dispatches it.
      clearError();
      // Update the state with the new date range
      setDatespan({ start: updatedStart, finish: updatedFinish });
      dispatch({ type: PortfolioShowLoadingActions.START_REQUEST });
    } catch (timespanError) {
      logger.error('Error updating timespan:', timespanError);
    }
  };

  const defaultData = useMemo(() => ({
    portfolio: { externalIdentifier: title, propertyMembers: {}, userMembers: {} },
    buy: { rules: {}, data: {} },
    sell: { rules: {}, data: {} },
    meters: {},
    properties: {},
    users: {},
  }), [title]);

  // Only build main data if we have a portfolio (pure — no side effects)
  const mainData = useMemo(() => {
    if (!combinedData) {
      return defaultData;
    }

    try {
      const { portfolio: portfolioData } = combinedData;

      if (!portfolioData) {
        return defaultData;
      }

      // Build the main data with the full portfolio
      const result = buildMainData(portfolioData, portfolioTimezone);

      // Log key metrics from the result
      const buyDataPoints = Object.keys(result?.buy?.data).length;
      const sellDataPoints = Object.keys(result?.sell?.data).length;

      logger.log(`Built main data with ${buyDataPoints} buy data points and ${sellDataPoints} sell data points`);
      return result;
    } catch (mainDataError) {
      logger.error('Error building main data:', mainDataError);
      return defaultData;
    }
  }, [combinedData, defaultData, portfolioTimezone]);

  // Manage overlay lifecycle: dispatch stage transitions and HIDE_OVERLAY
  // based on reducer state transitions, matching PropertyShow's pattern.
  // Each loading stage advances automatically so the checklist is visible
  // to the user — especially on cache hits where all dispatches are
  // synchronous and would otherwise flash too quickly.
  useEffect(() => {
    let timerId;

    if (loadingState.state === PortfolioShowLoadingStates.PREPARING_DATA) {
      dispatch({ type: PortfolioShowLoadingActions.DATA_PREPARED });
    } else if (loadingState.state === PortfolioShowLoadingStates.RENDERING) {
      dispatch({ type: PortfolioShowLoadingActions.RENDERING_DISPLAYED });
    } else if (loadingState.state === PortfolioShowLoadingStates.COMPLETE) {
      timerId = setTimeout(() => {
        dispatch({ type: PortfolioShowLoadingActions.HIDE_OVERLAY });
      }, MODAL_CLOSE_DELAY);
    }

    return () => clearTimeout(timerId);
  }, [loadingState.state]);

  // Portfolios always group by Trade Type. Show the grouping message
  // while the overlay is active, reset when overlay closes.
  useEffect(() => {
    const { IDLE, CANCELLED } = PortfolioShowLoadingStates;
    if (loadingState.state === IDLE || loadingState.state === CANCELLED) {
      showGroupingMessageRef.current = false;
    } else {
      showGroupingMessageRef.current = true;
    }
  }, [loadingState.state]);

  // Snapshot the current datespan into previousDatespanRef only when the
  // pipeline reaches COMPLETE with valid data. Because COMPLETE is only
  // reached after a cache segment has been committed, the snapshot is
  // guaranteed to correspond to a cached segment — so restoreLastSuccessfulLoad
  // can revert directly to it without any inverse time-range conversion.
  useEffect(() => {
    if (loadingState.state === PortfolioShowLoadingStates.COMPLETE && combinedData) {
      previousDatespanRef.current = {
        start: datespan.start,
        finish: datespan.finish,
      };
    }
  }, [loadingState.state, combinedData, datespan.start, datespan.finish]);

  // Overlay is visible during loading and briefly during COMPLETE (the
  // useEffect above keeps it visible for MODAL_CLOSE_DELAY before dispatching
  // HIDE_OVERLAY → IDLE). IDLE, ERROR, and CANCELLED are the terminal
  // states where the overlay is hidden.
  const dataLoading = loadingState.state !== PortfolioShowLoadingStates.IDLE
    && loadingState.state !== PortfolioShowLoadingStates.ERROR
    && loadingState.state !== PortfolioShowLoadingStates.CANCELLED;

  // The skeleton also masks the chart for the full duration of a retry. During a
  // retry the initial-load effect resets initialLoadComplete to false, which
  // tears down the previous date-range effect instance and dispatches a stray
  // CANCEL_REQUEST — briefly returning the reducer to the CANCELLED terminal
  // state. isRetrying stays true from triggerRetry until the replay resolves or
  // fails, so masking on it keeps the stale "no data" chart hidden across that
  // transient frame.
  const skeletonVisible = dataLoading || isRetrying;

  const renderPortfolioControls = (portfolioData, renderError) => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    const updatedStartTime = startTime.setZone(timezone).setLocale(locale);
    const updatedFinishTime = finishTime.setZone(timezone).setLocale(locale);

    // Large portfolio property size
    const largePortfolioPropertyCount = APIConfig().LARGE_PORTFOLIO_PROPERTY_COUNT || 50;

    // Set isLargePortfolio flag to true when property count exceeds
    // the configured large portfolio property count
    const isLargePortfolio = propertyCount >= largePortfolioPropertyCount;

    return (
      <DashboardControl
        chartView={chartView}
        chartViewSelector={
          (
            dropdownOpen,
            toggleFunc,
            chartViewData,
            setChartViewFunc,
            dataSource,
          ) => chartViewSelector(
            dropdownOpen,
            toggleFunc,
            chartViewData,
            setChartViewFunc,
            dataSource,
            isLargePortfolio,
          )
        }
        controlSetStateFunc={(opts) => controlSetStateFunc(opts)}
        downloadSelectorOpen={downloadSelectorOpen}
        error={renderError}
        mainData={portfolioData}
        minStart={active?.start}
        timespan={{ start: updatedStartTime, finish: updatedFinishTime }}
        timezone={timezone}
        setChartViewFunc={setChartView}
        source={source}
        timespanUpdateFunc={handleTimespanUpdate}
        toggleFunc={toggle}
        viewSelectorOpen={viewSelectorOpen}
        unit={unit}
        isModalOpen={dataLoading}
      />
    );
  };

  const { start: finalStart, finish: finalFinish } = datespan || {};

  // Create header inputs for the Heading subheading.
  const headerInputs = {
    uuid: uuid || '',
    propertyCount,
    timespan: {
      start: finalStart,
      finish: finalFinish,
    },
    userCount,
  };

  // Shared helper: attempts to recover the last successfully loaded data from
  // the cache. On a hit, restores the data, reverts the datespan (DatePicker +
  // URL), and flashes a success message. Reverting the datespan re-runs the
  // date-range effect; its synchronous cache hit dispatches MARK_FULLY_LOADED,
  // which the reducer's CANCELLED terminal guard blocks — so the restored data
  // renders without a loading frame.
  //
  // Both guards are required: `recovered` confirms the hook has a cached segment
  // (the hook's dateSpan is in time-range form and cannot be used for setDatespan
  // directly), and `previousDatespanRef.current` supplies the corresponding
  // date-range form snapshot written by the COMPLETE effect. If either is absent
  // there is no safe recovery point, so the function returns false.
  const restoreLastSuccessfulLoad = useCallback(() => {
    const recovered = getRecoveredSpanFromCache();
    if (recovered && previousDatespanRef.current) {
      applyRecoveredCacheData(recovered);
      setDatespan({
        start: previousDatespanRef.current.start,
        finish: previousDatespanRef.current.finish,
      });
      FlashesStore.flash(
        FlashesStore.SUCCESS,
        intl.formatMessage({
          id: 'portfolio.portfolio_show.cache_hit_warning.message',
          defaultMessage: 'Request cancelled. Last available data has been restored.',
        }),
      );
      return true;
    }
    return false;
  }, [intl, getRecoveredSpanFromCache, applyRecoveredCacheData]);

  // Cancel handler: cancels the in-flight fetch, dispatches CANCEL_REQUEST, then
  // falls back to the last successfully loaded data (reverting datespan + URL).
  // On a cache miss (first load cancel), clears combinedData so the chart shows
  // the noDataMessage instead of stale data filtered to the new unfetched range.
  const handleCancel = useCallback(() => {
    cancelFunc();
    dispatch({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });

    if (restoreLastSuccessfulLoad()) {
      return;
    }

    // No cached data to restore (e.g. first load cancelled) — confirm the
    // cancellation and clear data to avoid a stale chart (P2-1).
    applyRecoveredCacheData(null);
    FlashesStore.flash(
      FlashesStore.SUCCESS,
      intl.formatMessage({
        id: 'portfolio.portfolio_show.cancel.message',
        defaultMessage: 'Request has been successfully cancelled.',
      }),
    );
  }, [cancelFunc, intl, restoreLastSuccessfulLoad, applyRecoveredCacheData]);

  // Handles dismissal of the fetch-error modal. On a cache hit, restores the
  // last successfully loaded data and range. On a cache miss, returns false
  // without restoring data or flashing success. Note: PlaceholderErrorModal
  // always closes itself visually on dismiss regardless of this return value
  // (see PlaceholderErrorModal.jsx handleCancelFunc); the return value is not
  // used to gate modal visibility.
  const handleErrorDismiss = useCallback(
    () => restoreLastSuccessfulLoad(),
    [restoreLastSuccessfulLoad],
  );

  // Build loading overlay from state machine
  const { loadingOverlayProps } = buildLoadingOverlayState({
    intl,
    datespan,
    timezone,
    loadingState,
    showGroupingMessage: showGroupingMessageRef.current,
    onCancelFunc: handleCancel,
    isRetrying,
  });

  const pageTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.page.title', defaultMessage: 'Enosi - Portfolio: {portfolioTitle}' }, { portfolioTitle: title });
  const breadcrumbTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.breadcrumbs.portfolio.label', defaultMessage: 'Portfolios' });
  const overviewTitle = intl.formatMessage({ id: 'portfolio.portfolio_show.heading.overview.title', defaultMessage: 'Overview' });

  // When the user cancels the first load (no prior data), show a message
  // asking them to select a date and initiate a new request. Suppressed while a
  // retry is in flight so the "Request cancelled" text never replaces the
  // loading overlay during the retry fetch.
  const noDataMessage = loadingState.state === PortfolioShowLoadingStates.CANCELLED
    && !combinedData && !isRetrying
    ? intl.formatMessage({
      id: 'portfolio.portfolio_show.cancel.chart.no_data.message',
      defaultMessage: 'Request cancelled. Please select a date and initiate a new request.',
    })
    : null;

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      {/* Page structure always shown (visible behind overlay) */}
      <Breadcrumbs breadcrumbs={[{ name: breadcrumbTitle, path: '/portfolios' }, { name: title }]} />
      <Heading
        title={title}
        subtitle={uuid ? subHeading(headerInputs) : null}
        context={overviewTitle}
        controls={renderPortfolioControls(mainData, error)}
      />

      {error ? (
        /* Channel 1 (hook): an async fetch error from the batched pipeline
           replaces the chart content with a blocking retry/cancel modal. */
        <PlaceholderErrorModal
          bodyText={formatAsyncErrorBodyText(intl, messages, queryType)}
          error={error}
          onCancelFunc={handleErrorDismiss}
          onRetryFunc={handleRetry}
          placeholderComponent={Placeholder}
          retryDisabled={isRetrying}
        />
      ) : (
        <>
          {skeletonVisible && (
            <>
              <LoadingOverlay inputs={loadingOverlayProps} />
              <Placeholder />
            </>
          )}

          {/* Content: visible when not loading. Hidden during loading via
              visibility:hidden + position:absolute so it stays in the DOM
              (preserving React state) without affecting page layout.
              noDataMessage is shown inside the chart card when the user
              cancels the first load and there is no prior data. */}
          <div style={skeletonVisible ? { visibility: 'hidden', position: 'absolute' } : {}}>
            <PortfolioShowTradeEnergy
              chartView={chartView}
              controlSetStateFunc={controlSetStateFunc}
              handleTimespanUpdateFunc={handleTimespanUpdate}
              historianAggregation={aggregation}
              mainData={mainData}
              noDataMessage={noDataMessage}
              timespan={finalDatespan}
              timezone={timezone}
              tradeAggregation={aggregation}
              unit={unit}
              source={source}
              selectedKeys={selectedKeys}
              setSelectedKeysFunc={setSelectedKeys}
            />
          </div>
        </>
      )}
    </>
  );
}

export default PortfolioShow;

PortfolioShow.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }).isRequired,
  queryParams: PropTypes.shape({
    timezone: PropTypes.string,
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
    source: PropTypes.string,
    unit: PropTypes.string,
  }).isRequired,
};
